import { type ReactNode, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Sends the user to the Auth0 hosted login (which also hosts the password-reset flow) and captures where they were
 * headed so `StandardAuth0Provider`'s redirect callback can restore it.
 *
 * Guards on isAuthenticated so a user mid token-exchange — who can land here via a route guard before the authority
 * resolves — isn't bounced back to Auth0, and on a ref so a re-render can't fire a second redirect. Also guards on
 * the SDK's `error`: useAuth0AuthGate signs a failed session out, but React runs this child's effect before the
 * gate's, so without the guard we'd redirect the broken session back to the hosted login and loop.
 *
 * `returnTo` defaults to the current URL. Callers inside react-router should pass the path they want restored (e.g.
 * the `from` a route guard stashed in location state) since that's richer than what window.location shows.
 */
export const Auth0LoginRedirect = ({ returnTo, loader }: { returnTo?: string; loader?: ReactNode }) => {
    const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0();
    const redirectingRef = useRef(false);
    const destination = returnTo ?? window.location.pathname + window.location.search;

    useEffect(() => {
        if (isLoading || isAuthenticated || error || redirectingRef.current) {
            return;
        }

        redirectingRef.current = true;

        loginWithRedirect({ appState: { returnTo: destination } }).catch((error: unknown) => {
            console.error('Unable to redirect to login page', error);
            redirectingRef.current = false;
        });
    }, [isLoading, isAuthenticated, error, loginWithRedirect, destination]);

    return loader ?? null;
};
